import { inject, Injectable } from '@angular/core';
import { AppConfig } from '@mkp/shared/util-app-config';
import { APP_ENVIRONMENT_PROVIDER } from '@mkp/shared/util-environment';
import { captureException, init } from '@sentry/browser';
import { AppVersion } from '@mkp/shared/models';
import { getError } from './sentry.helpers';

@Injectable({ providedIn: 'root' })
export class SentryService {
  private readonly environment = inject(APP_ENVIRONMENT_PROVIDER);

  constructor() {
    init({
      dsn: AppConfig.sentry.dsn,
      environment: this.environment.name,
      ignoreErrors: ['Non-Error exception captured'],
      release: stripVersion(this.environment.version),
    });
  }

  captureException(error: unknown): void;
  captureException(origin: string, error?: unknown): void;
  captureException(originOrError: string | unknown, errorInput?: unknown): void {
    const error = getError(originOrError, errorInput);

    if (this.shouldCaptureException(error)) {
      captureException(error, { level: 'error' });
    }
  }

  captureWarning(error: unknown): void;
  captureWarning(origin: string, error?: unknown): void;
  captureWarning(originOrError: string | unknown, errorInput?: unknown): void {
    const error = getError(originOrError, errorInput);

    if (this.shouldCaptureException(error)) {
      captureException(error, { level: 'warning' });
    }
  }

  private shouldCaptureException(error: unknown): boolean {
    const isDevEnvironment = ['dev', 'qa'].includes(this.environment.name);
    if (isDevEnvironment) {
      console.error(`Error captured by sentry:\n`, error);
    }
    return !isDevEnvironment;
  }
}

function stripVersion(version: AppVersion) {
  const dash = version.indexOf('-');
  if (dash != -1) {
    const build = version.indexOf('.', dash);
    return version.substring(0, build != -1 ? build : version.length);
  }
  return version;
}
